import React from "react";
import { Link as MuiLink } from "@mui/material";
import { Paper, Grid, IconButton, Typography, Box } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { TravelProduct } from "@t3/shared";
import { useCartContext } from "@/context/CartContext";
import CardImage from "@/components/cardImage";
import convertToPoints from "@/utils/convertToPoints";
import { Link } from "react-router-dom";

interface CartTravelItemProps {
  product: TravelProduct;
  index: number;
}

export const CartTravelItem: React.FC<CartTravelItemProps> = ({
  product,
  index,
}) => {
  const { removeTravelItem, cartUpdateLoading } = useCartContext();

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 2,
        p: 2.5,
        borderRadius: 2,
        transition: "box-shadow 0.2s ease",
      }}
      data-cypress={`checkout-travel-item`}
    >
      <Grid container spacing={2} alignItems="flex-start">
        {/* Product Image */}
        <Grid>
          <div>
            <CardImage
              imageUrl={product.metadata.imageUrls.firebaseImage}
              height="82px"
              width="130px"
              margin={{ xs: "5px 5px 0 3px", sm: "auto" }}
            />
          </div>
        </Grid>

        {/* Product Details */}
        <Grid flex={1} sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{
              fontSize: 16,
              fontWeight: 600,
              mb: 0.5,
              lineHeight: 1.3,
            }}
            data-cypress="checkout-travel-item-title"
          >
            {product.metadata.brandName || "Product"}
          </Typography>
          <MuiLink
            component={Link}
            to={`/shop/travel/${product.metadata.brandKey}`}
            sx={{
              fontSize: 14,
              fontWeight: 500,
              textDecoration: "none",
              color: "primary.main",
              display: "inline-block",
              mb: 1,
            }}
            data-cypress="view-gift-card-page-link"
          >
            View travel package page
          </MuiLink>

          <Box sx={{ mt: 1.5, display: { xs: "none", sm: "block" } }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: "primary.main",
                fontSize: 18,
              }}
              data-cypress="checkout-travel-item-points"
            >
              {convertToPoints(product.amount)} Points
            </Typography>
          </Box>
        </Grid>

        {/* Delete Button */}
        <Grid sx={{ display: { xs: "none", sm: "block" } }}>
          <IconButton
            aria-label="delete"
            size="medium"
            data-cypress="remove-item-button"
            onClick={() => removeTravelItem(product.utid, index)}
            disabled={cartUpdateLoading}
            sx={{
              color: "error.main",
            }}
          >
            <DeleteIcon fontSize="medium" />
          </IconButton>
        </Grid>
      </Grid>
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2,
        }}
      >
        <Typography
          sx={{
            color: "red",
            cursor: "pointer",
          }}
          onClick={() => removeTravelItem(product.utid, index)}
          data-cypress="remove-item-button"
          component="span"
          fontSize={14}
        >
          REMOVE
        </Typography>

        <Box sx={{ textAlign: "right", display: "flex" }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "primary.main",
              fontSize: 18,
            }}
            data-cypress="checkout-travel-item-points"
          >
            {convertToPoints(product.amount)} Points
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};
